import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import { SerializedStyles } from "@emotion/react";
import { Text } from "@epignosis_llc/gnosis";
import { TrophySVG } from "@epignosis_llc/gnosis/icons";

import useProcessEvent from "@hooks/useProcessEvent";
import { useProductFruitsApiInstance } from "@hooks/useProductFruitsApiInstance";
import { useConfigurationStore, useUIStore } from "@stores";

import { EVENTS as PFEvents } from "@components/ProductFruits/constants";
import { EVENTS } from "@constants/processEvents";
import queryKeys from "@constants/queryKeys";

import { hasClickedOnToggleButton, setToggleButtonPressed } from "./helpers";
import { checklistToggleButtonContainer } from "./styles";
import { Checklist } from "../../types";

type ChecklistToggleButtonProps = {
  checklist: Checklist;
  isCollapsed?: boolean;
};

export const ChecklistToggleButton: React.FC<ChecklistToggleButtonProps> = ({
  checklist,
  isCollapsed = false,
}) => {
  const { toggleProductFruitsChecklist } = useUIStore();

  const [shouldGlow, setShouldGlow] = useState(false);

  const { handleProcessEvent } = useProcessEvent();

  const { fireEvent } = useProductFruitsApiInstance();

  const { domainSettings, userProfileData } = useConfigurationStore();
  const { reached_user_limit: userLimitReached } = domainSettings || {};
  const { can_create_course_plan: canClickAddCourseButton } = userProfileData?.policies || {};

  const queryClient = useQueryClient();

  useEffect(() => {
    setShouldGlow(!hasClickedOnToggleButton());
  }, []);

  const buttonHandler = (event: React.MouseEvent): void => {
    handleProcessEvent(EVENTS.START_ONBOARDING);

    queryClient.invalidateQueries(queryKeys.domainSettings);

    if (userLimitReached) {
      fireEvent(PFEvents.ADDED_USER);
    }

    if (!canClickAddCourseButton) {
      fireEvent(PFEvents.ADDED_COURSE);
    }

    event.stopPropagation();
    event.preventDefault();
    setToggleButtonPressed(true);
    setShouldGlow(false);
    toggleProductFruitsChecklist();
  };

  const handleMouseEvents = (event: React.MouseEvent): void => {
    event.stopPropagation();
  };

  const getButtonText = (): string => {
    const doneItems = checklist.items.filter((item) => item.state === "done").length;
    const totalItems = checklist.items.length;
    const buttonText = `${doneItems}/${totalItems}`;
    return buttonText;
  };

  return (
    <>
      {checklist && (
        <div
          id="pf-checklist-toggle-button"
          onClick={buttonHandler}
          onMouseOver={handleMouseEvents}
          onMouseEnter={handleMouseEvents}
          onMouseLeave={handleMouseEvents}
          css={(theme): SerializedStyles =>
            checklistToggleButtonContainer(theme, isCollapsed, shouldGlow)
          }
          role="button"
          aria-hidden="true"
          tabIndex={-1}
        >
          <TrophySVG height={32} className={"checklist-icon"} />
          <Text as="div" fontSize="sm" className="button-text">
            {getButtonText()}
          </Text>
        </div>
      )}
    </>
  );
};
